/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';

// The PR surfaces' mode set. 'mine'/'scope' predate the multi-mode control
// (the original two-segment pill) and persist as-is in storage;
// normalizePrMode maps any legacy or junk stored value onto the current set.
export type PrMode = 'mine' | 'authored' | 'review' | 'scope' | 'scope-review';

export const PR_MODE_LABELS: Record<PrMode, string> = {
  mine: 'Involves me',
  authored: 'Opened by me',
  review: 'Pending my review',
  scope: 'Scope repos',
  'scope-review': 'Scope pending review',
};

const PERSONAL: PrMode[] = ['mine', 'authored', 'review'];
const SCOPED: PrMode[] = ['scope', 'scope-review'];

export const normalizePrMode = (v: unknown): PrMode =>
  typeof v === 'string' && v in PR_MODE_LABELS ? (v as PrMode) : 'mine';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    background: 'transparent',
    color: theme.palette.text.secondary,
    fontFamily: 'inherit',
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1,
    padding: theme.spacing(0.75, 1.5),
    cursor: 'pointer',
    transition: 'background-color 120ms cubic-bezier(0.25, 1, 0.5, 1), border-color 120ms cubic-bezier(0.25, 1, 0.5, 1)',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.text.secondary,
      color: theme.palette.text.primary,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  chipActive: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 110, 91, 0.16)'
        : 'rgba(255, 110, 91, 0.10)',
    borderColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
    fontWeight: 600,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontVariantNumeric: 'tabular-nums',
  },
  countActive: {
    color: theme.palette.primary.light,
  },
  // Thin divider between the personal and scope groups; collapses with the
  // row's wrap so it never strands at a line edge.
  sep: {
    width: 1,
    alignSelf: 'stretch',
    margin: theme.spacing(0.5, 0.5),
    backgroundColor: theme.palette.divider,
  },
}));

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? '–' : n > 99 ? '99+' : String(n);

/**
 * Mode selector for the PR card and the /pull-requests page. Every mode stays
 * visible with a live count (the discoverability the two-segment pill had and
 * a menu loses); rendered as the card's subheader so it gets the full card
 * width instead of fighting the title for the action slot, and wraps cleanly
 * on narrow columns. Personal modes (data follows the user / View As lens)
 * sit left of the divider; scope modes (data follows the team scope) right.
 */
export const PrModeChips = ({
  mode,
  onChange,
  counts,
}: {
  mode: PrMode;
  onChange: (next: PrMode) => void;
  // Per-mode counts for the badges; null/undefined renders an en dash (still
  // loading or unavailable). The control never blocks on them.
  counts: Partial<Record<PrMode, number | null>>;
}) => {
  const classes = useStyles();

  const chip = (m: PrMode) => {
    const active = m === mode;
    return (
      <button
        key={m}
        type="button"
        aria-pressed={active}
        className={`${classes.chip}${active ? ` ${classes.chipActive}` : ''}`}
        onClick={() => onChange(m)}
      >
        {PR_MODE_LABELS[m]}
        <Typography
          component="span"
          className={`${classes.count}${active ? ` ${classes.countActive}` : ''}`}
        >
          {fmt(counts[m])}
        </Typography>
      </button>
    );
  };

  return (
    <Box role="group" aria-label="Pull request source" className={classes.row}>
      {PERSONAL.map(chip)}
      <span className={classes.sep} aria-hidden />
      {SCOPED.map(chip)}
    </Box>
  );
};
